import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldIcon, ZapIcon, HeartPulseIcon, BrainIcon, CircleIcon, FlaskConicalIcon, ShieldPlusIcon, BatteryChargingIcon, ArrowRightIcon, MoonIcon } from 'lucide-react';
const Reishi = () => {
  const [activeSection, setActiveSection] = useState('adaptogenic');
  return <div className="w-full bg-white" data-id="element-1150">
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center items-center text-center px-4 pt-24 pb-16 bg-gradient-to-b from-green-50 to-green-100" data-id="element-1151">
        <div className="max-w-4xl mx-auto" data-id="element-1152">
          <div className="w-20 h-1 bg-green-500 mx-auto mb-8 rounded-full" data-id="element-1153"></div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4" data-id="element-1154">
            Peace — Reishi
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto mb-10" data-id="element-1155">
            Calm focus with smoky notes.
          </p>
        </div>
      </section>

      {/* Benefits Categories Section with Side Navigation */}
      <section className="py-16 px-4 bg-white" data-id="element-1156">
        <div className="container mx-auto" data-id="element-1157">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-1158">
            Reishi Spora: 9 Benefit Categories
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto text-center mb-6" data-id="element-1159">
            Our proprietary extraction technology unlocks the full spectrum of
            Reishi bioactives, delivering nine distinct benefit categories in a
            single cup.
          </p>
          {/* Spec Summary Bar */}
          <div className="max-w-3xl mx-auto bg-gray-50 rounded-xl p-4 mb-16 text-center" data-id="element-1160">
            <p className="text-sm font-medium tracking-wide uppercase" data-id="element-1161">
              Reishi Spora | Adaptogenic Calm + Immunonutrients + Antioxidants
            </p>
          </div>

          {/* Desktop: Sticky side navigation */}
          <div className="hidden md:flex" data-id="element-1162">
            <div className="w-64 sticky top-32 self-start pr-8" data-id="element-1163">
              <nav className="flex flex-col space-y-4" data-id="element-1164">
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'adaptogenic' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('adaptogenic')} data-id="element-1165">
                  <div className="flex items-center gap-3" data-id="element-1166">
                    <MoonIcon className="w-5 h-5" data-id="element-1167" />
                    <span data-id="element-1168">Adaptogenic Calm</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'immunonutrients' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('immunonutrients')} data-id="element-1169">
                  <div className="flex items-center gap-3" data-id="element-1170">
                    <ShieldPlusIcon className="w-5 h-5" data-id="element-1171" />
                    <span data-id="element-1172">Immunonutrients</span>
                  </div>
                </button>
                <button className={`text-left px-4 py-2 rounded-lg transition-colors ${activeSection === 'antioxidants' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setActiveSection('antioxidants')} data-id="element-1173">
                  <div className="flex items-center gap-3" data-id="element-1174">
                    <ShieldIcon className="w-5 h-5" data-id="element-1175" />
                    <span data-id="element-1176">Antioxidants</span>
                  </div>
                </button>
              </nav>
            </div>
            <div className="flex-1 max-w-3xl" data-id="element-1177">
              {/* Adaptogenic Calm Section */}
              <section id="adaptogenic" className={`pb-24 ${activeSection === 'adaptogenic' ? 'block' : 'hidden'}`} data-id="element-1178">
                <h2 className="text-3xl font-bold mb-8" data-id="element-1179">
                  The Adaptogenic Calm of Reishi
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-1180">
                  Reishi normalizes stress response, improves mitochondrial
                  efficiency, and modulates GABA to relax the nervous system for
                  calm focus.
                </p>
                {/* 3-column layout for desktop */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1181">
                  {/* Adaptogens */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1182">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1183">
                      <CircleIcon className="w-6 h-6" data-id="element-1184" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1185">Adaptogens</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1186">
                      Stress relief
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1187">
                      <span className="font-medium" data-id="element-1188">Experience:</span> Stress
                      relief and mood balance.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1189">
                      <span className="font-medium" data-id="element-1190">Why it works:</span>{' '}
                      Triterpenes normalize stress response via the HPA axis.
                    </p>
                    <div className="mt-auto" data-id="element-1191">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1192">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1193">
                        <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-1194">
                          Triterpenes
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Bioenergetics */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1195">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1196">
                      <ZapIcon className="w-6 h-6" data-id="element-1197" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1198">Bioenergetics</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1199">
                      Steady energy
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1200">
                      <span className="font-medium" data-id="element-1201">Experience:</span> Steady
                      energy without overstimulation.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1202">
                      <span className="font-medium" data-id="element-1203">Why it works:</span>{' '}
                      Polysaccharides improve mitochondrial efficiency.
                    </p>
                    <div className="mt-auto" data-id="element-1204">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1205">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1206">
                        <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-1207">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Neuroprotective Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1208">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1209">
                      <BrainIcon className="w-6 h-6" data-id="element-1210" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1211">Neuroprotective</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1212">
                      Relaxed focus
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1213">
                      <span className="font-medium" data-id="element-1214">Experience:</span> Relaxed
                      focus and improved sleep quality.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1215">
                      <span className="font-medium" data-id="element-1216">Why it works:</span>{' '}
                      Ganoderic acids modulate GABA and relax the nervous
                      system.
                    </p>
                    <div className="mt-auto" data-id="element-1217">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1218">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1219">
                        <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-1220">
                          Ganoderic acids
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Immunonutrients Section */}
              <section id="immunonutrients" className={`pb-24 ${activeSection === 'immunonutrients' ? 'block' : 'hidden'}`} data-id="element-1221">
                <h2 className="text-3xl font-bold mb-8" data-id="element-1222">The Immune Balance</h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-1223">
                  Reishi contains unique compounds that support balanced
                  immunity, hormone balance, and cardiovascular ease.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1224">
                  {/* Immunonutrients */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1225">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1226">
                      <ShieldPlusIcon className="w-6 h-6" data-id="element-1227" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1228">Immunonutrients</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1229">
                      Balanced defense
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1230">
                      <span className="font-medium" data-id="element-1231">Experience:</span> Stronger
                      daily defenses, less overstimulation.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1232">
                      <span className="font-medium" data-id="element-1233">Why it works:</span>{' '}
                      β-glucans and ganoderic acids support balanced immunity.
                    </p>
                    <div className="mt-auto" data-id="element-1234">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1235">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1236">
                        <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-1237">
                          β-glucans
                        </span>
                        <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-1238">
                          Ganoderic acids
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Endocrine Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1239">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1240">
                      <FlaskConicalIcon className="w-6 h-6" data-id="element-1241" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1242">
                      Endocrine Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1243">
                      Metabolic flow
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1244">
                      <span className="font-medium" data-id="element-1245">Experience:</span> Calm,
                      clear, and restored metabolic flow.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1246">
                      <span className="font-medium" data-id="element-1247">Why it works:</span>{' '}
                      Triterpenes support liver detox and hormone balance.
                    </p>
                    <div className="mt-auto" data-id="element-1248">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1249">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1250">
                        <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-1251">
                          Triterpenes
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Circulatory Support */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1252">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1253">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-1254" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1255">
                      Circulatory Support
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1256">
                      Cardiovascular ease
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1257">
                      <span className="font-medium" data-id="element-1258">Experience:</span>{' '}
                      Cardiovascular ease and balance.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1259">
                      <span className="font-medium" data-id="element-1260">Why it works:</span> Sterols
                      support blood pressure regulation.
                    </p>
                    <div className="mt-auto" data-id="element-1261">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1262">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1263">
                        <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-1264">
                          Sterols
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Antioxidants Section */}
              <section id="antioxidants" className={`pb-24 ${activeSection === 'antioxidants' ? 'block' : 'hidden'}`} data-id="element-1265">
                <h2 className="text-3xl font-bold mb-8" data-id="element-1266">
                  The Protection Network
                </h2>
                <p className="text-lg text-gray-600 mb-12" data-id="element-1267">
                  Reishi contains powerful compounds that reduce oxidative
                  cellular stress, relieve tension, and support easier
                  breathing.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1268">
                  {/* Antioxidants */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1269">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1270">
                      <ShieldIcon className="w-6 h-6" data-id="element-1271" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1272">Antioxidants</h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1273">
                      Cellular calm
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1274">
                      <span className="font-medium" data-id="element-1275">Experience:</span> Promotes
                      calm and long-term cellular health.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1276">
                      <span className="font-medium" data-id="element-1277">Why it works:</span>{' '}
                      Triterpenes and polysaccharides reduce oxidative cellular
                      stress.
                    </p>
                    <div className="mt-auto" data-id="element-1278">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1279">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1280">
                        <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-1281">
                          Triterpenes
                        </span>
                        <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-1282">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Anti-Inflammatory */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1283">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1284">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-1285" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1286">
                      Anti-Inflammatory
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1287">
                      Tension relief
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1288">
                      <span className="font-medium" data-id="element-1289">Experience:</span> Relieves
                      tension in body and joints.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1290">
                      <span className="font-medium" data-id="element-1291">Why it works:</span>{' '}
                      Ganoderic acids suppress inflammatory pathways.
                    </p>
                    <div className="mt-auto" data-id="element-1292">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1293">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1294">
                        <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-1295">
                          Ganoderic acids
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Respiratory Health */}
                  <div className="bg-white rounded-xl p-6 shadow-sm" data-id="element-1296">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mb-4" data-id="element-1297">
                      <div className="w-6 h-6" data-id="element-1298" />
                    </div>
                    <h3 className="text-xl font-bold mb-1" data-id="element-1299">
                      Respiratory Health
                    </h3>
                    <p className="text-sm text-gray-500 font-medium mb-4" data-id="element-1300">
                      Easier breathing
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1301">
                      <span className="font-medium" data-id="element-1302">Experience:</span> Easier
                      breath, less internal tension.
                    </p>
                    <p className="text-gray-600 mb-4" data-id="element-1303">
                      <span className="font-medium" data-id="element-1304">Why it works:</span>{' '}
                      Polysaccharides calm airways and support breathing.
                    </p>
                    <div className="mt-auto" data-id="element-1305">
                      <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-1306">
                        Key Compounds:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-1307">
                        <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium" data-id="element-1308">
                          Polysaccharides
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* Mobile: Collapsible sections */}
          <div className="md:hidden space-y-8" data-id="element-1309">
            {/* Spec Summary Bar - Mobile */}
            <div className="bg-gray-50 rounded-xl p-4 mb-8 text-center" data-id="element-1310">
              <p className="text-xs font-medium tracking-wide uppercase" data-id="element-1311">
                Reishi Spora | Adaptogenic Calm + Immunonutrients + Antioxidants
              </p>
            </div>
            <div data-id="element-1312">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'adaptogenic' ? '' : 'adaptogenic')} data-id="element-1313">
                <div className="flex items-center gap-3" data-id="element-1314">
                  <MoonIcon className="w-5 h-5" data-id="element-1315" />
                  <h2 className="text-xl font-bold" data-id="element-1316">Adaptogenic Calm</h2>
                </div>
                <span data-id="element-1317">{activeSection === 'adaptogenic' ? '−' : '+'}</span>
              </button>
              {activeSection === 'adaptogenic' && <div className="p-4 space-y-8" data-id="element-1318">
                  {/* Mobile content for Adaptogenic Calm section */}
                </div>}
            </div>
            <div data-id="element-1319">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'immunonutrients' ? '' : 'immunonutrients')} data-id="element-1320">
                <div className="flex items-center gap-3" data-id="element-1321">
                  <ShieldPlusIcon className="w-5 h-5" data-id="element-1322" />
                  <h2 className="text-xl font-bold" data-id="element-1323">The Immune Balance</h2>
                </div>
                <span data-id="element-1324">{activeSection === 'immunonutrients' ? '−' : '+'}</span>
              </button>
              {activeSection === 'immunonutrients' && <div className="p-4 space-y-8" data-id="element-1325">
                  {/* Mobile content for Immunonutrients section */}
                </div>}
            </div>
            <div data-id="element-1326">
              <button className="flex items-center justify-between w-full text-left p-4 bg-gray-100 rounded-xl" onClick={() => setActiveSection(activeSection === 'antioxidants' ? '' : 'antioxidants')} data-id="element-1327">
                <div className="flex items-center gap-3" data-id="element-1328">
                  <ShieldIcon className="w-5 h-5" data-id="element-1329" />
                  <h2 className="text-xl font-bold" data-id="element-1330">The Protection Network</h2>
                </div>
                <span data-id="element-1331">{activeSection === 'antioxidants' ? '−' : '+'}</span>
              </button>
              {activeSection === 'antioxidants' && <div className="p-4 space-y-8" data-id="element-1332">
                  {/* Mobile content for Antioxidants section */}
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA Section */}
      <section className="py-16 px-4 bg-black text-white text-center" data-id="element-1333">
        <div className="max-w-4xl mx-auto" data-id="element-1334">
          <h2 className="text-3xl font-bold mb-6" data-id="element-1335">Experience Reishi Spora</h2>
          <p className="text-xl max-w-2xl mx-auto mb-8" data-id="element-1336">
            Calm focus with smoky notes that helps you find balance in a hectic
            world.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1337">
            <Link to="/#products" className="bg-green-500 text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-green-600 transition-colors" data-id="element-1338">
              Get Spora
            </Link>
            <Link to="/how-it-works" className="bg-transparent text-white border border-white px-8 py-3 rounded-full text-sm font-medium hover:bg-white/10 transition-colors" data-id="element-1339">
              Learn The Ritual
            </Link>
            <Link to="/" className="bg-white text-black px-8 py-3 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors inline-flex items-center justify-center" data-id="element-1340">
              <span data-id="element-1341">Explore All Varieties</span>
              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1342" />
            </Link>
          </div>
        </div>
      </section>
    </div>;
};
export default Reishi;